import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Box, Container, Grid, Paper, Typography, useTheme, CircularProgress, Alert, Button, IconButton } from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';
import { UserType } from '../../data/types/auth';
import { StatsWidget } from './components/StatsWidget';
import { GraphWidget } from './components/GraphWidget';
import { DataTableWidget } from './components/DataTableWidget';
import { DateRangeSelector } from './components/DateRangeSelector';
import { PageGradient } from '../../components/PageGradient';
import { styled } from '@mui/material/styles';
import { businessService } from '../../services/businessService';
import { Business } from '../../data/types/domain';
import { Add as AddIcon, Edit as EditIcon } from '@mui/icons-material';
import { analyticsService } from '../../services/analyticsService';
import { formatDateToYYYYMMDD, YYYYMMDDToDate } from '../../utils/dateUtils';
import { EmptyStateContainer } from './components/EmptyStateContainer';
import { calculatePercentageChange } from '../../utils/common';
import { BusinessForm } from '../business-listing/components/BusinessForm';
import { BusinessListingResult } from '../../data/types/application';

// Create a styled container like the comparison page
const StyledContainer = styled(Container)(({ theme }) => ({
  maxWidth: '1200px',
  padding: theme.spacing(4),
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(2),
  },
}));

export const DashboardPage: React.FC = () => {
  const { userType, sub } = useAuth();
  const [searchParams] = useSearchParams();
  const businessId = searchParams.get('business_id');
  const [dateRange, setDateRange] = useState<{ start: Date; end: Date, range: number }>({
    start: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000), // 2 days ago
    end: new Date(),
    range: 1,
  });
  const [businessListing, setBusinessListing] = useState<BusinessListingResult>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [businessHits, setBusinessHits] = useState<number>(0);
  const [change, setChange] = useState<string>('0%');
  const [editDialogOpen, setEditDialogOpen] = useState(false);

  const fetchBusiness = async () => {
    try {
      setLoading(true);
      if (businessId) {
        const data = await businessService.getBusinessById(parseInt(businessId));
        setBusinessListing(data);
        setLoading(false);
      } else if (sub) {
        const data = await businessService.getBusinessListingsByUserId(sub);
        setBusinessListing(data);
        setLoading(false);
      } else {
        setError('Business ID not found');
      }
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch business');
    } finally {
      setLoading(false);
    }
  };


  useEffect(() => {
    fetchBusiness();
  }, [sub]);

  useEffect(() => {
    if (businessListing) {
      fetchBusinessHits(dateRange);
    }
  }, [businessListing]);

  if (loading) {
    return (
      <EmptyStateContainer>
        <CircularProgress size={60} />
        <Typography variant="h6" color="text.secondary" sx={{ mt: 2 }}>
          Loading your dashboard...
        </Typography>
      </EmptyStateContainer>
    );
  }


  if (userType?.toLowerCase() !== UserType.BUSINESS && !businessId) {
    return (
      <EmptyStateContainer>
        <Alert
          severity="warning"
          sx={{
            width: '100%',
            maxWidth: '600px',
            '& .MuiAlert-message': {
              width: '100%',
            }
          }}
        >
          <Typography variant="h6" gutterBottom>
            Access Restricted
          </Typography>
          <Typography variant="body1" color="text.secondary">
            This dashboard is only available for business accounts.
          </Typography>
        </Alert>
      </EmptyStateContainer>
    );
  }

  if (userType?.toLowerCase() === UserType.BUSINESS && !businessListing) {
    return (
      <EmptyStateContainer>
        <AddIcon />
        <Typography variant="h5" gutterBottom>
          No Business Listings Yet
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          Contact us to create your first business listing
        </Typography>
      </EmptyStateContainer>
    );
  }

  if (error) {
    return (
      <EmptyStateContainer>
        <Alert
          severity="error"
          sx={{
            width: '100%',
            maxWidth: '600px',
            '& .MuiAlert-message': {
              width: '100%',
            }
          }}
        >
          <Typography variant="h6" gutterBottom>
            Oops! Something went wrong
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {error}
          </Typography>
          <Button
            variant="contained"
            color="primary"
            sx={{ mt: 2 }}
            onClick={() => window.location.reload()}
          >
            Try Again
          </Button>
        </Alert>
      </EmptyStateContainer>
    );
  }

  const fetchBusinessHits = async (range: { start: Date; end: Date, range: number }) => {
    // Here we add a extra range to the start date to get the previous period
    // i.e. if range is 7day, then we get the previous 7 days, so a total of 14 days.
    // This way we can compare the previous period to the current period to see the change.
    const startDate = new Date(range.start.getTime() - range.range * 24 * 60 * 60 * 1000);
    const businessHits = await analyticsService.getBusinessHits(businessListing?.business.id.toString() || '', {
      start: formatDateToYYYYMMDD(startDate),
      end: formatDateToYYYYMMDD(range.end),
    });

    // const totalClicks = businessHits?.reduce((sum: number, hit: { click_count: number }) => sum + hit.click_count, 0) || 0;
    const percentageChange = businessHits ? calculatePercentageChange(businessHits, { start: startDate, end: range.end }) : { total: 0, change: '0%' };
    setBusinessHits(percentageChange.total);
    setChange(percentageChange.change);
  };

  const handleDateRangeChange = async (newDateRange: { start: Date; end: Date, range: number }) => {
    setDateRange(newDateRange);
    fetchBusinessHits(newDateRange);
  };

  return (
    <Box sx={{ position: 'relative', minHeight: '100vh', pb: 6 }}>
      <PageGradient />
      <StyledContainer>
        <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h4" component="h1" gutterBottom>
            Welcome {businessListing?.business.name}
          </Typography>
          {userType?.toLowerCase() === UserType.BUSINESS && (
            <IconButton
              onClick={() => setEditDialogOpen(true)}
              color="primary"
              sx={{
                backgroundColor: 'primary.main',
                color: 'white',
                '&:hover': {
                  backgroundColor: 'primary.dark',
                },
              }}
            >
              <EditIcon />
            </IconButton>
          )}
        </Box>

        <Box sx={{ mb: 4 }}>
          <DateRangeSelector
            value={dateRange}
            onChange={handleDateRangeChange}
          />
        </Box>

        <Grid container spacing={3}>
          {/* Stats Widgets */}
          {/* <Grid item xs={12} md={4}>
            <StatsWidget
              title="Total Visitors"
              value="1402"
              change="+42%"
              dateRange={dateRange}
              infoText="Total number of people who have visited the comparison website during the selected period."
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <StatsWidget
              title="Total Impressions"
              value="560"
              change="+12%"
              dateRange={dateRange}
              infoText="Number of times your website was displayed to visitors during the selected period."
            />
          </Grid> */}
          <Grid item xs={12} md={4}>
            <StatsWidget
              title="Website Clicks"
              value={businessHits}
              change={change}
              dateRange={dateRange}
              infoText="Total number of clicks on your website during the selected period. This includes all page views and interactions."
            />
          </Grid>

          {/* Graph Widget */}
          {/* <Grid item xs={12}>
            <GraphWidget
              title="Revenue Trend"
              dateRange={dateRange}
            />
          </Grid> */}

          {/* Data Table Widget */}
          {/* <Grid item xs={12}>
            <DataTableWidget
              title="Recent Orders"
              dateRange={dateRange}
            />
          </Grid> */}
        </Grid>
      </StyledContainer>

      {businessListing && (
        <BusinessForm
          open={editDialogOpen}
          onClose={() => {
            setEditDialogOpen(false);
            fetchBusiness();
          }}
          onUpdate={() => fetchBusiness()}
          initialData={businessListing}
          title="Edit Business"
          isEditMode={true}
        />
      )}
    </Box>
  );
}; 